/* eslint-disable no-console */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import moment from "moment";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CardTitle from "components/CardTitle";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";

import DataTable from "components/DataTable";

import { deleteProjectImage } from "store/actions/mainUserActions";

import { useSelector, useDispatch, connect } from "react-redux";

import { getAllServices, updateService, removeServices } from "store/actions/servicesActions";
import ServiceCategories from "./ServicesCategories";
import serviceUtils from "./serviceUtils";
import ServiceForm from "./ServiceForm";

const { formatTimeToFormatedString } = serviceUtils;

function CellDate({ value }) {
  return <span>{value ? moment(value).format("DD/MM/YYYY HH:mm") : ""}</span>;
}
CellDate.propTypes = { value: PropTypes.number };
CellDate.defaultProps = { value: 0 };

function CellStartEndTime({ value }) {
  return <span>{value || ""}</span>;
}
CellStartEndTime.propTypes = { value: PropTypes.string };
CellStartEndTime.defaultProps = { value: "" };

function CellDurationTime({ value }) {
  return <span>{`${value || 0} min`}</span>;
}
CellDurationTime.propTypes = { value: PropTypes.number };
CellDurationTime.defaultProps = { value: 0 };

function CellFormatedTime({ value }) {
  return <span>{formatTimeToFormatedString(value)}</span>;
}
CellFormatedTime.propTypes = {
  value: PropTypes.number.isRequired,
};

function AlertDialog({ open, handleClose, handleAgree }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={handleClose}>
      <MDBox
        sx={({ palette: { white, background } }) => ({
          backgroundColor: darkMode ? background.sidenav : white.main,
        })}
      >
        <DialogTitle>{t("confirmation")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("stepNotUndo")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose}>{t("cancel")}</MDButton>
          <MDButton onClick={handleAgree} autoFocus>
            {t("OK")}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}
AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAgree: PropTypes.func,
};
AlertDialog.defaultProps = {
  handleAgree: () => {},
};

function Services({
  database,
  services,
  projectId,
  actGetServices,
  actUpdateService,
  actRemoveServices,
  mainUserId,
}) {
  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [uids, setUids] = useState([]);
  const [actionType, setActionType] = useState("");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (mainUserId) {
      actGetServices(mainUserId);
    }
  }, [mainUserId, actGetServices]);

  const columns = () => [
    { Header: t("name"), accessor: "name" },
    { Header: t("serviceDuration"), accessor: "serviceDuration", Cell: CellDurationTime },
    { Header: t("createdAt"), accessor: "createdAt", Cell: CellDate },
    { Header: t("cost"), accessor: "cost" },
    { Header: t("currency"), accessor: "currency" },
    { Header: t("startTime"), accessor: "startTime", Cell: CellStartEndTime },
    { Header: t("endTime"), accessor: "endTime", Cell: CellStartEndTime },
    { Header: t("minPreviousTime"), accessor: "minPreviousTime", Cell: CellFormatedTime },
    { Header: t("minCancelTime"), accessor: "minCancelTime", Cell: CellFormatedTime },
  ];

  const remove = (data) => {
    setUids(data.map((d) => d.original));
    setActionType("remove");
    setOpenConfirm(true);
  };

  const onSubmit = (data) => {
    if (actionType === "edit" || actionType === "add")
      actUpdateService({
        mainUserId,
        projectId,
        serviceData: data,
      });

    setOpen(false);
  };

  const deleteImagePromise = (imageUid) =>
    new Promise((resolve) => {
      dispatch(
        deleteProjectImage({
          imageUid,
          projectId,
          cb: () => resolve(true),
        })
      );
    });

  const confirm = async () => {
    if (actionType !== "remove") return;

    const selected = Array.isArray(uids) ? uids : [];
    const imageUids = selected.map((s) => s?.imageUid).filter(Boolean);

    await Promise.all(imageUids.map(deleteImagePromise));

    actRemoveServices({
      mainUserId,
      projectId,
      services: selected,
    });

    setOpenConfirm(false);
    setUids([]);
    setActionType("");
  };

  const add = () => {
    setActionType("add");
    setUids([]);
    setOpen(true);
  };

  const edit = (indexes) => {
    const ids = indexes.map((i) => i.original.uid);
    setUids(ids);
    setActionType("edit");
    setOpen(true);
  };

  const menuConfig = useSelector((state) => state.mainUserReducer.customApp.menuConfig);

  const servicesLabel = t(
    menuConfig?.services?.label ?? menuConfig?.services?.defaultLabel ?? "services"
  );

  return (
    <Card>
      <ServiceCategories database={database} projectId={projectId} mainUserId={mainUserId} />

      <MDBox mt={3} />

      <AlertDialog
        open={openConfirm}
        handleClose={() => setOpenConfirm(false)}
        handleAgree={confirm}
      />

      <Modal open={open} onClose={() => setOpen(false)} style={{ overflow: "scroll" }}>
        <MDBox
          mt={3}
          sx={{
            width: { xs: "90vw", md: "65vw" },
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <ServiceForm
            onSubmit={onSubmit}
            actionType={actionType}
            uids={uids}
            projectId={projectId}
          />
        </MDBox>
      </Modal>

      <CardTitle title={servicesLabel} icon="room_service" bgColor="warning" />

      <DataTable
        table={{ columns: columns(), rows: Object.values(services || {}) }}
        canSearch
        canSelect
        canEdit
        canAdd
        canActuate
        action={(data) => {
          const uid = data?.original?.uid;
          if (uid) navigate(`/projects/${projectId}/service/${uid}`);
        }}
        actionIcon="arrow_forward_ios"
        actionColor="info"
        add={add}
        edit={edit}
        remove={remove}
      />
    </Card>
  );
}

Services.propTypes = {
  services: PropTypes.object,
  projectId: PropTypes.string,
  database: PropTypes.object.isRequired,
  actUpdateService: PropTypes.func.isRequired,
  actGetServices: PropTypes.func.isRequired,
  actRemoveServices: PropTypes.func.isRequired,
  mainUserId: PropTypes.string.isRequired,
};

Services.defaultProps = {
  services: {},
  projectId: "",
};

const mapStateToProps = (state, ownProps) => {
  const { servicesReducer, projectsReducer } = state;
  const { projectId } = ownProps;

  const allServices = servicesReducer?.services || {};
  const project = projectsReducer ? projectsReducer[projectId] : {};
  const servicesIds = project?.services || {};

  const projectServices = {};

  Object.keys(servicesIds).forEach((id) => {
    if (allServices[id]) {
      projectServices[id] = allServices[id];
    }
  });

  return {
    services: projectServices,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actGetServices: () => dispatch(getAllServices()),
  actUpdateService: (data) => dispatch(updateService(data)),
  actRemoveServices: (data) => dispatch(removeServices(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Services);
